import { ifAll, ifAny, IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    PrCaregivingTypeEntity,
    PrCSSZSubmissionCaregivingDetailEntity,
    PrCSSZSubmissionCaregivingEntity,
    PrCSSZSubmissionPaymentBasisCaregivingEntity,
    PrMpsvRelationEntity,
    PrWorkedInDaysIntervalEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { addCareReceiverDefinitions } from "@pages/payroll/socialEvents/SocialEventsDef";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import {
    addBasicBenefitRequestGroup,
    addCaregivingActions,
    addCaregivingIntervalFields,
    addCommonNEMPRIDefinitions,
    addCSSZSwitchDef,
    addMaternityRelatedFieldDefinitions,
    addReturnToWorkFields,
    addStudyFieldDefinitions,
    addWorkLeaveWithoutPayFieldDefinitions,
    CSSZTranslationFiles,
    getConditionalFastEntryListRenderer,
    isDurationType,
    isEndType,
    isStartType,
    WorkedInDaysLocalPath
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrCSSZSubmissionCaregivingFormView
    from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionCaregivingFormView";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType } from "../../../../enums";
import {
    addFormDef,
    addFormGroup,
    addRowsToGroup,
    enhanceGroupFieldsVisibility
} from "../../../../views/formView/Form.utils";

const _detailPath = (path: PrCSSZSubmissionCaregivingDetailEntity, ...restProps: string[]) =>
    createPath(PrCSSZSubmissionCaregivingEntity.Detail, path, ...restProps);
const _paymentBasisPath = (path: PrCSSZSubmissionPaymentBasisCaregivingEntity, ...restProps: string[]) =>
    _detailPath(PrCSSZSubmissionCaregivingDetailEntity.PaymentBasis, path, ...restProps);

export const hasOtherCareGiverBenefitClaim: TGetValueFn<boolean> = ({ storage, bindingContext }) =>
    !!storage.getValue(bindingContext.getParent().navigate(PrCSSZSubmissionCaregivingDetailEntity.HasOtherCareGiverBenefitClaim));

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrCSSZSubmissionCaregivingFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication,
        subType: PrSubmissionSubtypeCode.NempriCaregiving
    }));

    // shared definitions with other CSSZ submissions
    addCommonNEMPRIDefinitions(d, context, args);

    // **************** CareGiving actions ****************
    addCaregivingActions(d, PrCSSZSubmissionCaregivingDetailEntity);

    // **************** Extend Employer confirmation group ****************
    // Student status
    const studyRows = addStudyFieldDefinitions(d, PrCSSZSubmissionCaregivingDetailEntity);
    // Work leave without pay
    const workLeaveWithoutPayRows = addWorkLeaveWithoutPayFieldDefinitions(d, PrCSSZSubmissionCaregivingDetailEntity);
    // Maternity related fields
    const maternityRelatedRows = addMaternityRelatedFieldDefinitions(d, PrCSSZSubmissionCaregivingDetailEntity);

    // Enhance group def
    const rows = [
        ...studyRows,
        ...workLeaveWithoutPayRows,
        ...maternityRelatedRows
    ];
    addRowsToGroup(d, PrSubmissionGroup.IncomeInPeriod, rows);
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.IncomeInPeriod, isStartType);
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.PaymentMethod, isStartType);

    // **************** BenefitRequest ****************
    addFormDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.ReasonForCare), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:SocialEventsForm.ReasonCaregiving"),
        width: BasicInputSizes.XXL,
        defaultValue: null,
        isVisible: isStartType,
        fieldSettings: {
            displayName: PrCaregivingTypeEntity.Name,
            noRecordText: i18next.t("Common:Select.NoRecord"),
        },
        affectedFields: []
    });
    addBasicBenefitRequestGroup(d, [[
        { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.ReasonForCare) }
    ]], {
        forceVisibilityWithoutFields: true,
        isVisible: ifAny(isStartType, isDurationType, isEndType)
    });

    // **************** CareReceiver definition ****************
    const careReceiverRows = addCareReceiverDefinitions(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.CareReceiver));
    addFormDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.CareReceiverRelation), {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XL,
        isRequired: true,
        fieldSettings: {
            displayName: PrMpsvRelationEntity.Name
        }
    });
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.DoesCareReceiverLiveInSameHousehold), "DoesCareReceiverLiveInSameHousehold");
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsLoneCaregiver), "IsLoneCaregiver");
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.HasChildUnder16InSchool), "HasChildUnder16InSchool");

    // **************** BenefitRequest - fake empty group to show form title ****************
    addFormGroup(d, {
        id: PrSubmissionGroup.CareReceiver,
        title: i18next.t("Submission:SocialEventsForm.CareReceiver"),
        showGroupDivider: false,
        rows: [
            ...careReceiverRows,
            [{ id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.CareReceiverRelation) }],
            [
                { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.DoesCareReceiverLiveInSameHousehold) },
                { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsLoneCaregiver) },
                { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.HasChildUnder16InSchool) },
            ]
        ]
    }, PrSubmissionGroup.BenefitRequest);
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.CareReceiver, isStartType);

    // **************** Caregiving Intervals ****************
    addCaregivingIntervalFields(d, PrCSSZSubmissionCaregivingDetailEntity, { labelPrefix: "Caregiving" });

    // **************** Additional benefit request fields ****************
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.HasOtherCareGiverBenefitClaim), "HasOtherCareGiverBenefitClaim", [
        _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsOtherCareGiverWorking),
        _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsOtherCareGiverIncapacitated)
    ], {
        isVisible: isStartType,
    });
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsOtherCareGiverWorking), "IsOtherCareGiverWorking", {
        isVisible: ifAll(isStartType, hasOtherCareGiverBenefitClaim)
    });
    addCSSZSwitchDef(d, _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsOtherCareGiverIncapacitated), "IsOtherCareGiverIncapacitated", {
        isVisible: ifAll(isStartType, hasOtherCareGiverBenefitClaim)
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitRequestFooter,
        showGroupDivider: false,
        rows: [[
                { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.HasOtherCareGiverBenefitClaim) },
                { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsOtherCareGiverWorking) },
                { id: _detailPath(PrCSSZSubmissionCaregivingDetailEntity.IsOtherCareGiverIncapacitated) },
            ]]
    }, PrSubmissionGroup.CaregivingIntervals);


    // **************** Benefit Payment Inputs group ****************
    addCSSZSwitchDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.HasPlannedShifts), "HasPlannedShifts", [
        _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.HasWorkedOnPlannedShifts)
    ], {
        isVisible: ifAny(isDurationType, isEndType),
    });
    addCSSZSwitchDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.HasWorkedOnPlannedShifts), "HasWorkedOnPlannedShifts", {
        isVisible: ifAll(ifAny(isDurationType, isEndType), ({ storage, bindingContext }: IGetValueArgs) =>
            !!storage.getValue(bindingContext.getParent().navigate(PrCSSZSubmissionPaymentBasisCaregivingEntity.HasPlannedShifts)))
    });
    // line items with dates...
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.WorkedInDays, PrWorkedInDaysIntervalEntity.DateFrom), {
        label: i18next.t("Submission:SocialEventsForm.From"),
        isVisible: ifAny(isDurationType, isEndType),
    });
    addFormDef(d, _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.WorkedInDays, PrWorkedInDaysIntervalEntity.DateTo), {
        label: i18next.t("Submission:SocialEventsForm.To"),
        isVisible: ifAny(isDurationType, isEndType),
    });
    addCSSZSwitchDef(d, WorkedInDaysLocalPath, "EmployeeWorkedInDaysCaregiving");

    addFormGroup(d, {
        id: PrSubmissionGroup.BenefitPaymentInputs,
        title: i18next.t("Submission:SocialEventsForm.BenefitPaymentInputs"),
        isVisible: ifAny(isDurationType, isEndType),
        isSmallBottomMargin: true,
        rows: [
            [{ id: _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.HasPlannedShifts) }],
            [{ id: _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.HasWorkedOnPlannedShifts) }],
            [{ id: WorkedInDaysLocalPath }],
        ],
        lineItems: {
            collection: _paymentBasisPath(PrCSSZSubmissionPaymentBasisCaregivingEntity.WorkedInDays),
            order: PrWorkedInDaysIntervalEntity.Id,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            canAdd: (args) => !!args.storage.getValueByPath(WorkedInDaysLocalPath),
            columns: [
                { id: PrWorkedInDaysIntervalEntity.DateFrom },
                { id: PrWorkedInDaysIntervalEntity.DateTo }
            ],
            render: getConditionalFastEntryListRenderer(WorkedInDaysLocalPath)
        }
    }, PrSubmissionGroup.BenefitRequestFooter);

    // **************** Benefit Payment Inputs - return to work ****************
    addReturnToWorkFields(d, PrCSSZSubmissionCaregivingDetailEntity, { addDateReturnToWork: false });
    enhanceGroupFieldsVisibility(d, PrSubmissionGroup.BenefitPaymentInputsReturnToWork, isEndType);

    return {
        ...d,
        entitySet: EntitySetName.PrCSSZSubmissionsCaregiving
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrCSSZSubmissionCaregiving, getDefinitions);